import { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { JacKeyIcon } from '@/components/wizard/JacKeyIcon';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useEventTracking } from '@/hooks/useEventTracking';
import type { Announcement } from '@/types/announcement';

/* ─── Copy variations by draft age ───────────────────────── */
const nudgeCopy = {
  en: {
    recent:   'You started something meaningful recently.',
    medium:   'Some messages take courage. Yours is still waiting.',
    old:      'Your unfinished message is still here whenever you\'re ready.',
    continue: 'Continue Writing',
    dismiss:  'Dismiss',
    jackey:   'JacKey',
  },
  es: {
    recent:   'Empezaste algo significativo recientemente.',
    medium:   'Algunos mensajes requieren valentía. El tuyo sigue esperando.',
    old:      'Tu mensaje sin terminar sigue aquí cuando estés listo.',
    continue: 'Continuar Escribiendo',
    dismiss:  'Descartar',
    jackey:   'JacKey',
  },
  fr: {
    recent:   'Vous avez commencé quelque chose de significatif récemment.',
    medium:   'Certains messages demandent du courage. Le vôtre attend toujours.',
    old:      'Votre message inachevé est toujours là quand vous serez prêt.',
    continue: 'Continuer à Écrire',
    dismiss:  'Ignorer',
    jackey:   'JacKey',
  },
  pt: {
    recent:   'Você começou algo significativo recentemente.',
    medium:   'Algumas mensagens exigem coragem. A sua ainda está esperando.',
    old:      'Sua mensagem inacabada ainda está aqui quando estiver pronto.',
    continue: 'Continuar Escrevendo',
    dismiss:  'Dispensar',
    jackey:   'JacKey',
  },
  ar: {
    recent:   'بدأت شيئًا ذا معنى مؤخرًا.',
    medium:   'بعض الرسائل تحتاج شجاعة. رسالتك لا تزال تنتظر.',
    old:      'رسالتك غير المكتملة لا تزال هنا عندما تكون مستعدًا.',
    continue: 'تابع الكتابة',
    dismiss:  'تجاهل',
    jackey:   'JacKey',
  },
  ja: {
    recent:   '最近、意味のある何かを始めましたね。',
    medium:   '勇気が必要なメッセージもあります。あなたのメッセージはまだ待っています。',
    old:      '未完成のメッセージは、準備ができたときにここにあります。',
    continue: '書き続ける',
    dismiss:  '閉じる',
    jackey:   'JacKey',
  },
  ko: {
    recent:   '최근 의미 있는 무언가를 시작하셨네요.',
    medium:   '어떤 메시지는 용기가 필요해요. 당신의 메시지가 기다리고 있어요.',
    old:      '미완성 메시지가 준비되었을 때 여기 있을 거예요.',
    continue: '이어 쓰기',
    dismiss:  '닫기',
    jackey:   'JacKey',
  },
  id: {
    recent:   'Anda baru memulai sesuatu yang bermakna.',
    medium:   'Beberapa pesan butuh keberanian. Pesanmu masih menunggu.',
    old:      'Pesan yang belum selesai masih di sini saat Anda siap.',
    continue: 'Lanjut Menulis',
    dismiss:  'Abaikan',
    jackey:   'JacKey',
  },
  hi: {
    recent:   'आपने हाल ही में कुछ सार्थक शुरू किया।',
    medium:   'कुछ संदेशों में हिम्मत लगती है। आपका अभी भी इंतज़ार कर रहा है।',
    old:      'आपका अधूरा संदेश अभी भी यहां है जब आप तैयार हों।',
    continue: 'लिखना जारी रखें',
    dismiss:  'खारिज करें',
    jackey:   'JacKey',
  },
};

function getAgeCategory(updatedAt: string): 'recent' | 'medium' | 'old' {
  const hoursSince = (Date.now() - new Date(updatedAt).getTime()) / 3_600_000;
  if (hoursSince < 72) return 'medium';
  return 'old';
}

interface JacKeyDraftNudgeProps {
  drafts: Announcement[];
}

export function JacKeyDraftNudge({ drafts }: JacKeyDraftNudgeProps) {
  const { language } = useLanguage();
  const { trackEvent } = useEventTracking();
  const [dismissed, setDismissed] = useState(false);
  const hasTrackedView = useRef(false);

  // Find the most recently edited draft that is older than 24 hours
  const mostRecentDraft = useMemo(() => {
    if (drafts.length === 0) return null;
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const eligibleDrafts = drafts.filter(
      (d) => new Date(d.updated_at).getTime() < oneDayAgo
    );
    if (eligibleDrafts.length === 0) return null;
    return [...eligibleDrafts].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )[0];
  }, [drafts]);

  const c = lc(nudgeCopy, language);
  const ageCategory = mostRecentDraft ? getAgeCategory(mostRecentDraft.updated_at) : 'medium';
  const message = c[ageCategory];
  const dismissKey = mostRecentDraft ? `jackey_draft_nudge_${mostRecentDraft.id}` : '';

  const sessionDismissed = typeof window !== 'undefined' && dismissKey && sessionStorage.getItem(dismissKey);

  // Track view once
  useEffect(() => {
    if (!hasTrackedView.current && mostRecentDraft && !dismissed && !sessionDismissed) {
      hasTrackedView.current = true;
      trackEvent('draft_nudge_shown', { draft_id: mostRecentDraft.id, age_category: ageCategory });
    }
  }, [mostRecentDraft, ageCategory, trackEvent, dismissed, sessionDismissed]);

  if (!mostRecentDraft || dismissed || sessionDismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(dismissKey, '1');
    trackEvent('draft_dismissed', { draft_id: mostRecentDraft.id, age_category: ageCategory });
  };

  const handleContinue = () => {
    trackEvent('draft_nudge_clicked', { draft_id: mostRecentDraft.id, age_category: ageCategory });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -24 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-lg"
      >
        <div
          className="relative rounded-2xl border border-primary/15 shadow-lg overflow-hidden backdrop-blur-sm"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--card)), hsl(var(--card)) 60%, hsl(var(--primary) / 0.06))',
          }}
        >
          {/* Top gradient accent */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

          <div className="flex items-start gap-3 p-4">
            {/* JacKey icon */}
            <div className="shrink-0 mt-0.5">
              <motion.div
                animate={{ y: [0, -2, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              >
                <JacKeyIcon size="sm" />
              </motion.div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground leading-relaxed">
                {message}
              </p>

              <div className="flex items-center gap-3 mt-2.5">
                <Link to={`/edit/${mostRecentDraft.id}`} onClick={handleContinue}>
                  <Button size="sm" className="rounded-xl gap-1.5 text-xs h-7 px-3">
                    {c.continue}
                  </Button>
                </Link>
                <button
                  onClick={handleDismiss}
                  className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                >
                  {c.dismiss}
                </button>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="shrink-0 h-6 w-6 flex items-center justify-center rounded-full hover:bg-accent transition-colors"
              aria-label={c.dismiss}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground/50" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
